import React from 'react'
import type { Witness } from '../../store/booksStore'
import { getWitnessActions, WitnessAction } from '../books/WitnessActionButton'
import './WitnessCardCompact.css'

interface WitnessCardCompactProps {
    witness: Witness
    chapterId: string
    bookId: string
    onClick?: () => void
    onAction?: (action: WitnessAction, witness: Witness) => void
}

type WitnessStatus = 'processed' | 'processing' | 'pending'

const WitnessCardCompact: React.FC<WitnessCardCompactProps> = ({
    witness,
    chapterId,
    bookId,
    onClick,
    onAction
}) => {
    const [showMenu, setShowMenu] = React.useState(false)

    // Determine status based on preprocessing result
    const getStatus = (): WitnessStatus => {
        if (!witness.preprocessing_result) return 'pending'
        if (witness.preprocessing_result.primary_chunking?.total_primary_chunks) {
            return 'processed'
        }
        return 'processing'
    }

    const status = getStatus()

    // Comprehensive chunk count logic (matching legacy WitnessCard)
    const getChunkCount = (): number => {
        const prep = witness.preprocessing_result
        if (!prep) return 0

        // Kimi preprocessing: chunks array
        if (prep.chunks?.length) return prep.chunks.length

        // Two-pass preprocessing: secondary_chunking.chunks (preferred)
        if (prep.secondary_chunking?.chunks?.length) {
            return prep.secondary_chunking.chunks.length
        }

        // Two-pass preprocessing: primary_chunking.chunks
        if (prep.primary_chunking?.chunks?.length) {
            return prep.primary_chunking.chunks.length
        }

        // Fall back to statistics if available
        if (prep.statistics?.total_chunks) {
            return prep.statistics.total_chunks
        }

        // Two-pass: total counts
        if (prep.secondary_chunking?.total_secondary_chunks) {
            return prep.secondary_chunking.total_secondary_chunks
        }

        if (prep.primary_chunking?.total_primary_chunks) {
            return prep.primary_chunking.total_primary_chunks
        }

        return 0
    }

    // Get micro-units count from segmentation result
    const getUnitCount = (): number => {
        return witness.segmentation_result?.aggregated_statistics?.total_units || 0
    }

    const chunkCount = getChunkCount()
    const unitCount = getUnitCount()
    const progress = status === 'processed' ? 100 : status === 'processing' ? 45 : 0

    // Extract library name from full library string
    const libraryName = witness.library || 'Unknown Library'

    // Get status text
    const getStatusText = (): string => {
        if (witness.processing_status === 'completed') return 'Verified'
        if (witness.processing_status === 'error') return 'Error'
        if (witness.processing_status === 'preprocessing' || witness.processing_status === 'segmenting') return 'Processing...'
        if (witness.processing_status === 'paused') return 'Paused'
        if (witness.processing_status === 'preprocessed') return 'Ready to segment'
        if (status === 'processed') return 'Ready to segment'
        if (status === 'processing') return 'Preprocessing...'
        return 'Not started'
    }

    // Get counts display string
    const getCountsDisplay = (): string => {
        if (chunkCount > 0 && unitCount > 0) {
            return `${chunkCount} chunks · ${unitCount} units`
        }
        if (chunkCount > 0) {
            return `${chunkCount} chunks`
        }
        return '—'
    }

    // Get available actions based on witness state
    const actions = getWitnessActions(witness)

    // Get primary action based on state
    const getPrimaryAction = (): WitnessAction | undefined => {
        switch (witness.processing_status) {
            case 'pending': return actions.find(a => a.type === 'preprocess')
            case 'preprocessed': return actions.find(a => a.type === 'segment')
            case 'completed': return actions.find(a => a.type === 'view')
            case 'error': return actions.find(a => a.type === 'restart')
            default: return undefined
        }
    }

    const primaryAction = getPrimaryAction()

    // Get action label
    const getActionLabel = (): string | null => {
        if (!primaryAction) return null
        switch (primaryAction.type) {
            case 'preprocess': return 'Start'
            case 'segment': return 'Segment'
            case 'view': return 'View'
            case 'restart': return 'Retry'
            case 'pause': return 'Pause'
            case 'resume': return 'Resume'
            default: return primaryAction.label
        }
    }

    const handleClick = () => {
        if (onClick) {
            onClick()
        } else {
            // Default navigation to segment editor
            window.location.hash = `#/books/${bookId}/chapters/${chapterId}/witnesses/${witness.id}/segment`
        }
    }

    const handleAction = (action: WitnessAction, e: React.MouseEvent) => {
        e.stopPropagation()
        if (onAction) {
            onAction(action, witness)
        } else {
            // Default action handling - navigate based on action type
            switch (action.type) {
                case 'preprocess':
                case 'segment':
                    window.location.hash = `#/books/${bookId}/chapters/${chapterId}/witnesses/${witness.id}/segment`
                    break
                case 'view':
                    window.location.hash = `#/books/${bookId}/chapters/${chapterId}/witness/${witness.id}/results`
                    break
                case 'delete':
                    if (window.confirm(`Delete witness "${witness.sigla}"?`)) {
                        // Would need deleteWitness from store
                        console.log('Delete witness:', witness.id)
                    }
                    break
                default:
                    console.log('Action:', action.type, witness.id)
            }
        }
        setShowMenu(false)
    }

    const statusClass = witness.processing_status === 'completed' ? 'status-complete' :
        witness.processing_status === 'error' ? 'status-error' :
            (witness.processing_status === 'preprocessing' || witness.processing_status === 'segmenting') ? 'status-processing' :
                `status-${status}`

    return (
        <div
            className={`witness-card-compact ${statusClass}`}
            onClick={handleClick}
        >
            <div className="card-stripe" />

            <div className="card-content">
                <div className="card-header-row">
                    <div className="witness-siglum">
                        {witness.sigla.charAt(0).toUpperCase()} — {witness.sigla}
                    </div>
                    {/* Primary Action Button */}
                    {getActionLabel() && (
                        <button
                            className="card-action-btn"
                            onClick={(e) => primaryAction && handleAction(primaryAction, e)}
                        >
                            {getActionLabel()}
                        </button>
                    )}
                </div>
                <div className="witness-library">{libraryName}</div>
            </div>

            <div className="card-progress-track">
                <div
                    className="card-progress-fill"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="card-footer">
                {!showMenu ? (
                    /* Normal view: status + count + "···" */
                    <>
                        <span className={`footer-status ${statusClass}`}>{getStatusText()}</span>
                        <div className="footer-meta">
                            <span className="meta-counts">{getCountsDisplay()}</span>
                            <button
                                className="menu-toggle"
                                onClick={(e) => { e.stopPropagation(); setShowMenu(true) }}
                            >
                                ···
                            </button>
                        </div>
                    </>
                ) : (
                    /* Expanded view: action buttons */
                    <div className="action-menu">
                        <button
                            className="menu-back"
                            onClick={(e) => { e.stopPropagation(); setShowMenu(false) }}
                        >
                            ←
                        </button>
                        <div className="action-buttons">
                            {actions.filter(a => a !== primaryAction).slice(0, 4).map((action, idx) => (
                                <button
                                    key={idx}
                                    className={`action-btn ${action.variant === 'destructive' ? 'destructive' : ''}`}
                                    onClick={(e) => handleAction(action, e)}
                                >
                                    {action.label.replace('Witness', '').replace('Results', '').replace('to Pending', '').trim()}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default WitnessCardCompact

